import React, { useCallback } from 'react';

import { useA11ySelector } from '@sg/a11y-helpers';
import { useSafeIsFocused, useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { Home } from '@order/features/home';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import { FloatingButtons, OrderStatusBar } from '../../../../components';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { type useHomeContent, useHomeNavigation } from '../../hooks';
import { HomeLoyaltyButton } from '../HomeLoyaltyButton';
import { HomeScreenBanner } from '../HomeScreenBanner';

export const HomeScreenContentXS = (props: HomeScreenContentProps) => {
  const { homeContent, bagPressed, ctaPressed } = props;

  const { track } = useTelemetry();
  const isFocused = useSafeIsFocused();
  const isLoggedIn = useIsLoggedIn();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { navigateToAccount, navigateToLoyalty } = useHomeNavigation();

  const a11ySelector = useA11ySelector('home.container');
  const contentA11ySelector = useA11ySelector('home.hero.text-content');
  const imageA11ySelector = useA11ySelector('home.hero.img');

  const shouldRenderScanButton = isLoggedIn;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onScanPress = useCallback(() => {
    if (isExpoRouterEnabled) {
      router?.navigate('/scan-at-checkout');
    } else {
      navigation?.navigate('Modal', { screen: 'ScanAtCheckout' });
    }

    track('home.scan.cta_tapped');
  }, [isExpoRouterEnabled, navigation, router, track]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      {isFocused && !isExpoRouterEnabled ? (
        <OrderStatusBar
          barStyle={homeContent.themeXS === 'light' ? 'light-content' : 'dark-content'}
        />
      ) : null}

      <Home.ContainerXS {...a11ySelector.props}>
        <Home.HeroImageXS
          imageUrl={homeContent.mainImageXS}
          imageA11yLabel={homeContent.mainImageA11yXS}
          {...imageA11ySelector.props}
        >
          <Home.ContentWrapperXS {...contentA11ySelector.props}>
            <Home.HeaderXS
              onPressAccount={navigateToAccount}
              onPressScan={shouldRenderScanButton ? onScanPress : undefined}
            >
              <Home.EyebrowText
                text={homeContent.eyebrowText}
                fontColor={homeContent.eyebrowTextColorXS}
              />

              <Home.HeroText text={homeContent.heroText} fontColor={homeContent.heroTextColorXS} />

              {homeContent.infoText ? (
                <Home.InfoText
                  text={homeContent.infoText}
                  fontColor={homeContent.infoTextColorXS}
                />
              ) : null}

              <Home.HeaderCta
                accessibilityLabel={homeContent.ctaText}
                deepLink={homeContent.ctaDeepLink}
                onPressCta={ctaPressed}
              >
                {homeContent.ctaText}
              </Home.HeaderCta>
            </Home.HeaderXS>
          </Home.ContentWrapperXS>
        </Home.HeroImageXS>
      </Home.ContainerXS>

      <FloatingButtons.Container anchor="full">
        <Home.FloatingButtons>
          <HomeLoyaltyButton fallbackMessage={homeContent.ctaLoyalty} onPress={navigateToLoyalty} />

          <FloatingButtons.Bag showMessage={false} onPress={bagPressed} />
        </Home.FloatingButtons>

        <HomeScreenBanner />
      </FloatingButtons.Container>
    </>
  );
};

type HomeScreenContentProps = {
  homeContent: ReturnType<typeof useHomeContent>['homeContent'];
  ctaPressed: () => void;
  bagPressed: () => void;
};
